import { useMutation, useQueryClient } from '@tanstack/react-query';
import { InferType } from 'yup';
import {
  APIError,
  APIFieldErrorContext,
  isAPIError,
} from 'shared/api/APIError';
import { APIResponse, requestCarrierAPI } from 'shared/api/CarrierAPIClient';
import { yupEnum, yupObject, yupString } from 'shared/utils/YupUtils';

export type SendVerificationCodeMethod = 'email' | 'sms';

const sendVerificationCodeMethods = ['email', 'sms'] as const;

interface SendVerificationCodeBody {
  method: SendVerificationCodeMethod;
}

interface SendVerificationCodeResponse {
  recipient: string;
}

export function useSendChangeBankAccountCode() {
  return useMutation({
    mutationFn: (
      method: SendVerificationCodeMethod,
    ): Promise<APIResponse<SendVerificationCodeResponse>> => {
      const body: SendVerificationCodeBody = { method };
      return requestCarrierAPI<APIResponse<SendVerificationCodeResponse>>(
        'POST /internal/web/payments/bank-accounts/send-verification-code/',
        { json: body },
      );
    },
  });
}

export type ChangeBankAccountPayload = InferType<
  typeof changeBankAccountPayloadSchema
>;
const changeBankAccountPayloadSchema = yupObject({
  holderName: yupString(),
  accountNumber: yupString(),
  routingNumber: yupString(),
  code: yupString(),
  verificationMethod: yupEnum(sendVerificationCodeMethods, 'email'),
});

type ChangeBankAccountRequestBody = InferType<
  typeof changeBankAccountRequestBodySchema
>;
const changeBankAccountRequestBodySchema = yupObject({
  account_number: yupString(),
  holder_name: yupString(),
  routing_number: yupString(),
  verification_code: yupString(),
  verification_method: yupEnum(sendVerificationCodeMethods, 'email'),
});

export function isInvalidCodeError(
  error: Error,
): error is APIError<APIFieldErrorContext> {
  return (
    isAPIError<APIFieldErrorContext>(error) &&
    Array.isArray(error.context?.code)
  );
}

export function useChangeBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      code,
      holderName,
      accountNumber,
      routingNumber,
      verificationMethod,
    }: ChangeBankAccountPayload): Promise<APIResponse<null>> => {
      const body: ChangeBankAccountRequestBody = {
        account_number: accountNumber,
        holder_name: holderName,
        routing_number: routingNumber,
        verification_code: code,
        verification_method: verificationMethod,
      };
      return requestCarrierAPI<APIResponse<null>>(
        'POST /internal/web/payments/bank-accounts/',
        { json: body },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['superpay-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['settings', 'carrier'] });
      void queryClient.invalidateQueries({
        queryKey: ['superpay', 'moov-information'],
      });
    },
  });
}
